import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { formatCommasNumber } from "@/common/util/format-number-with-commas";
import { Button, Checkbox, Modal, TextInput } from "flowbite-react";
import { useSetRecoilState } from "recoil";
import { ItemDetailLineDto } from "../../types/dto/item-detail-line-dto";
import { GrayMinusButton } from "../button/GrayMinusButton";
import { GrayPlusButton } from "../button/GrayPlusButton";
import { Body14RegularInput } from "../input/Body14RegularInput";
import { Body14Medium } from "../typography/Body14Medium";
import { Body14Regular } from "../typography/Body14Regular";
import { EditOutlineIcon } from "../icon/EditOutlineIcon";
import { ButtonColor } from "@/common/enum/button-color";
import { H4 } from "../typography/H4";
import { useEffect, useState } from "react";
import { toastState } from "@/atoms/system/toast-state";
import { Body14Strong } from "../typography/Body14Strong";

interface Props {
  cartItem: ItemDetailLineDto;
  checkedCartItems: ItemDetailLineDto[];
  handleCheckItem: () => void;
  memoValues: string[];
}

export const PartnerProductOrderCartItemCard: React.FC<Props> = ({
  cartItem,
  checkedCartItems,
  handleCheckItem,
}) => {
  const setToast = useSetRecoilState(toastState);
  const isChecked = checkedCartItems.some(
    (checkedCartItem) => checkedCartItem.childItem.id === cartItem.childItem.id
  );
  const setCartItems = useSetRecoilState(
    selectedPartnerProductOrderCartItemsState
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [tempMemoItems, setTempMemoItems] = useState<
    Array<{ name: string; value: string }>
  >([]);

  useEffect(() => {
    if (isModalOpen) {
      setTempMemoItems(cartItem.childItem.memoItems || []);
      setIsModified(false);
    }
  }, [isModalOpen]);

  const handleMinusButtonClick = () => {
    setCartItems((prevCartItems) =>
      prevCartItems.map((line) =>
        line.childItem.id === cartItem.childItem.id
          ? {
              ...line,
              quantity: Math.max(
                line.quantity - 1,
                line.childItem.minOrderQuantity
              ),
            }
          : line
      )
    );
  };

  const handlePlusButtonClick = () => {
    setCartItems((prevCartItems) =>
      prevCartItems.map((line) =>
        line.childItem.id === cartItem.childItem.id
          ? { ...line, quantity: line.quantity + 1 }
          : line
      )
    );
  };

  const handleQuantityChange = (value: string) => {
    const numberValue = Number(value);

    setCartItems((prevCartItems) =>
      prevCartItems.map((line) =>
        line.childItem.id === cartItem.childItem.id
          ? {
              ...line,
              quantity: numberValue,
            }
          : line
      )
    );
  };

  const handleMemoModify = () => {
    setIsModalOpen(true);
  };

  const handleMemoChange = (index: number, value: string) => {
    const newMemoItems = tempMemoItems.map((item, i) =>
      i === index ? { ...item, value } : item
    );
    setTempMemoItems(newMemoItems);

    const isChanged = cartItem.childItem.memoItems?.some(
      (item, i) => item.value !== newMemoItems[i].value
    );
    setIsModified(!!isChanged);
  };

  const handleSaveClick = () => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.childItem.id === cartItem.childItem.id
          ? {
              ...item,
              childItem: {
                ...item.childItem,
                memoItems: tempMemoItems,
              },
            }
          : item
      )
    );

    setToast({
      show: true,
      messages: ["주문 메모를 수정했습니다."],
    });
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-center py-[12px] px-[16px] w-full gap-[8px]">
          <Checkbox
            color={isChecked ? "dark" : ""}
            checked={isChecked}
            onChange={handleCheckItem}
            className="w-[16px] h-[16px]"
          />
          {cartItem.childItem.imageUrls.length > 0 && (
            <img
              src={cartItem.childItem.imageUrls[0]}
              alt={cartItem.childItem.name}
              className="w-[32px]"
            />
          )}
          <Body14Regular
            className={`text-semantic-text-body ${
              cartItem.childItem.imageUrls.length > 0
                ? "w-[167px]"
                : "w-[207px]"
            } truncate`}
          >
            {cartItem.childItem.name}
          </Body14Regular>
          <div className="flex flex-col items-center justify-center  w-[89px]">
            <div className="flex">
              <GrayMinusButton handleClick={handleMinusButtonClick} />
              <Body14RegularInput
                minNumber={cartItem.childItem.minOrderQuantity}
                className="text-semantic-text-body mx-[10px]"
                onChange={(value) => handleQuantityChange(value)}
              >
                {cartItem.quantity}
              </Body14RegularInput>
              <GrayPlusButton handleClick={handlePlusButtonClick} />
            </div>
            <div className="flex">
              <Body14Medium className="text-secondary-2-red">
                {cartItem.childItem.totalDiscountRate}%{" "}
              </Body14Medium>
              <Body14Medium className="text-gray-900 ml-[4px]">
                {formatCommasNumber(cartItem.childItem.totalPrice)}원
              </Body14Medium>
            </div>
          </div>
        </div>
        {(cartItem.childItem.memoItems?.length ?? 0) > 0 && (
          <div className="w-80 self-center">
            <div className="bg-grayscale-contrast-5 py-1 px-3 rounded-t-lg">
              <div className="flex items-center justify-between w-full">
                <Body14Medium>메모 정보</Body14Medium>
                <Button
                  onClick={handleMemoModify}
                  className="!bg-transparent hover:!bg-transparent"
                >
                  <EditOutlineIcon
                    size={20}
                    color="text-grayscale-contrast-80"
                  />
                </Button>
              </div>
            </div>
            <div className="bg-grayscale-contrast-2 py-2 max-h-[200px] overflow-y-auto rounded-b-lg border-t border-gray-300">
              {cartItem.childItem.memoItems?.map((memoItem, index) => (
                <div key={index} className="flex flex-col gap-2 py-1 px-3">
                  <div>
                    <Body14Strong className="text-semantic-text-secondary min-w-[80px]">
                      {memoItem.name}
                    </Body14Strong>
                  </div>
                  <div>
                    <Body14Regular>{memoItem.value}</Body14Regular>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <Modal size="2xl" show={true} onClose={() => setIsModalOpen(false)}>
          <Modal.Header>
            <H4 className="text-semantic-text-heading">상품 메모 수정하기</H4>
          </Modal.Header>
          <Modal.Body className="max-h-[400px] overflow-y-auto">
            <div className="flex flex-col gap-4">
              <div className="p-4 bg-gray-50">
                상품 메모 입력이 필요한 상품입니다. <br />
                견적 발행 및 발주 이후 수정이 불가하므로 정확한 정보 입력
                부탁드립니다.
              </div>
              {cartItem.childItem.memoItems?.map((memoItem, index) => (
                <div key={index} className="flex flex-col gap-2">
                  <span className="min-w-[80px]">{memoItem.name}</span>
                  <TextInput
                    value={tempMemoItems[index]?.value || ""}
                    onChange={(e) => handleMemoChange(index, e.target.value)}
                    placeholder="정보를 입력해주세요."
                    className="flex-1"
                    maxLength={100}
                  />
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              color={isModified ? ButtonColor.PRIMARY : ButtonColor.DISABLED}
              onClick={handleSaveClick}
              disabled={!isModified}
            >
              수정하기
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
